import {
    type ReactNode,
} from 'react'

import {
    CircleAlert,
    CircleCheck,
    CircleIcon,
    TriangleAlert,
} from 'lucide-react'

export type AlertVariant =
    | 'info'
    | 'success'
    | 'warning'
    | 'danger'

export interface AlertProps {
    children: ReactNode
    title?: ReactNode
    variant?: AlertVariant
    className?: string
}

export function Alert({
    children,
    title,
    variant = 'info',
    className,
}: AlertProps) {
    const isSuccess =
        variant === 'success'

    const isWarning =
        variant === 'warning'

    const isDanger =
        variant === 'danger'

    const role =
        isDanger
            ? 'alert'
            : 'status'

    const variantClasses =
        isDanger
            ? [
                  'border-(--color-danger-border)',
                  'bg-(--color-danger-muted)',
                  'text-(--color-danger)',
              ].join(' ')
            : isWarning
              ? [
                    'border-(--color-warning-border)',
                    'bg-(--color-warning-muted)',
                    'text-(--color-warning)',
                ].join(' ')
              : isSuccess
                ? [
                      'border-(--color-success-border)',
                      'bg-(--color-success-muted)',
                      'text-(--color-success)',
                  ].join(' ')
                : [
                      'border-(--color-primary-muted)',
                      'bg-(--color-primary-muted)',
                      'text-(--color-primary)',
                  ].join(' ')

    const Icon =
        isDanger
            ? CircleAlert
            : isWarning
              ? TriangleAlert
              : isSuccess
                ? CircleCheck
                : CircleIcon

    return (
        <div
            role={role}
            className={[
                'flex items-start gap-3',
                'rounded-xl border p-4',
                variantClasses,
                className,
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <Icon
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 shrink-0"
            />

            <div className="min-w-0">
                {title && (
                    <div className="text-sm font-semibold">
                        {title}
                    </div>
                )}

                <div
                    className={[
                        'text-sm leading-6',
                        title
                            ? 'mt-1'
                            : '',
                    ].join(' ')}
                >
                    {children}
                </div>
            </div>
        </div>
    )
}